import { I18n } from "@assets/strings";
import { Range } from "immutable";
import React from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { Organization } from "@domain/organization/organization";
import { useFeed, usePostCreation } from "@domain/post/hooks";
import { Post } from "@domain/post/post";
import { Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { gap } from "@ui/styles/utils";
import { Secondary } from "@ui/components/containers/textBlocks";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Stack } from "@ui/components/layout";
import { EmptyFeedIncentive } from "./emptyFeedIncentive";
import { FeedArticleSuggestions } from "@ui/components/post/feedSuggestions/feedArticleSuggestions";
import { FeedCourseSuggestions } from "@ui/components/post/feedSuggestions/feedCourseSuggestions";
import { FeedGroupSuggestions } from "@ui/components/post/feedSuggestions/feedGroupSuggestions";
import { FeedOrganizationSuggestions } from "@ui/components/post/feedSuggestions/feedOrganizationSuggestions";
import { FeedQuizSuggestions } from "@ui/components/post/feedSuggestions/feedQuizSuggestions";
import { FeedShareroomSuggestions } from "@ui/components/post/feedSuggestions/feedShareroomSuggestions";
import { FeedUserSuggestions } from "@ui/components/post/feedSuggestions/feedUserSuggestions";
import { PostView, PostViewSkeleton } from "./postView";
// import { FeedNews } from "./feedSuggestions/feedNews";

interface FeedListProps extends React.HTMLAttributes<HTMLDivElement> {
	user?: Person | Organization;
	onPostShared?: (sharedPost: Post) => void;
	groupId?: string;
	isFeed?: boolean;
}

const suggestionInterval = 5;

export const FeedList: React.FC<FeedListProps> = ({ user, groupId, isFeed = false, ...htmlProps }) => {
	const isOrganization = user?.type === "COMPANY" || user?.type === "INSTITUTE";
	const currentProfile = useCurrentProfile();
	const createPost = usePostCreation(groupId);
	const alias = useOrganizationAlias();
	const isMe = !user || user.id === currentProfile?.id || alias?.id === user.id;

	const {
		result: feed,
		fetchMore,
		loading,
		moreLoading,
		totalElements,
		lastPage,
	} = useFeed(groupId ?? user?.id, groupId ? "group" : isOrganization ? "organization" : "user", alias?.id);

	if (totalElements === 0) {
		return isMe ? (
			<EmptyFeedIncentive onCreatePost={createPost} {...htmlProps} />
		) : (
			<Secondary {...htmlProps}>{I18n.t("feed.empty", { user: user ? fullName(user) : "" })}</Secondary>
		);
	}

	return (
		<Container {...htmlProps}>
			<InfiniteScroll onEndReached={fetchMore} loading={loading || moreLoading}>
				<Feed>
					{/* <FeedNews /> */}

					{feed.map((post, i) => {
						return !user && !groupId && i > 0 && i % suggestionInterval === 0 ? (
							<React.Fragment key={post.id}>
								{i % (suggestionInterval * 6) === 0 ? (
									<FeedOrganizationSuggestions />
								) : i % (suggestionInterval * 7) === suggestionInterval * 6 ? (
									<FeedGroupSuggestions />
								) : i % (suggestionInterval * 6) === suggestionInterval * 5 ? (
									<FeedQuizSuggestions />
								) : i % (suggestionInterval * 6) === suggestionInterval * 4 ? (
									<FeedUserSuggestions />
								) : i % (suggestionInterval * 6) === suggestionInterval * 3 ? (
									<FeedArticleSuggestions />
								) : i % (suggestionInterval * 6) === suggestionInterval * 2 ? (
									<FeedCourseSuggestions />
								) : (
									isFeed && <FeedShareroomSuggestions />
								)}
								<PostView post={post} />
							</React.Fragment>
						) : post ? (
							<PostView key={post.id} post={post} />
						) : (
							<></>
						);
					})}
					{loading && Range(0, 2).map((i) => <PostViewSkeleton key={i} />)}
					{!lastPage && !!totalElements && (
						<Stack align="center">
							<ClipLoader size={48} />
						</Stack>
					)}
				</Feed>
			</InfiniteScroll>
		</Container>
	);
};

const Container = styled.div``;

const Feed = styled.div`
	${gap(20)};

	@media (max-width: 900px) {
		${gap(8)};
	}
`;

// const News = styled(FeedArticleSuggestions)``;
